import { ModalProps } from '@mui/material';
import { useCallback, useState } from 'react';

import { ApiWorkflowDialogSteps } from './enums';
import { useApiWorkflowDialogButtons } from './useApiWorkflowDialogButtons';
import { computeDialogSize } from '../../dialogHelpers';
import { ApiWorkflowDialogProps } from '../../types';

export interface UseApiWorkflowDialogProps
  extends Pick<
    ApiWorkflowDialogProps,
    | 'dialogMessages'
    | 'isValidForSubmit'
    | 'isInitialLoading'
    | 'initialLoadingError'
    | 'isSubmitting'
    | 'submittingError'
    | 'saveButtonColor'
    | 'onClose'
    | 'onSubmit'
    | 'onRedirectAfterSubmit'
    | 'onRetryInitialLoadingFetch'
    | 'onRetrySubmit'
    | 'successCountdownTime'
    | 'hideDisplayButtonsOnSuccess'
    | 'closeOnBackdropClick'
    | 'size'
  > {
  /** Whether the dialog should show the confirmation step */
  shouldShowConfirmationStep?: boolean;
}

export const useApiWorkflowDialog = ({
  dialogMessages,
  isValidForSubmit,
  isInitialLoading,
  initialLoadingError,
  isSubmitting,
  submittingError,
  saveButtonColor = 'primary',
  onClose,
  onSubmit,
  onRedirectAfterSubmit,
  onRetryInitialLoadingFetch,
  onRetrySubmit,
  successCountdownTime,
  shouldShowConfirmationStep,
  hideDisplayButtonsOnSuccess = false,
  closeOnBackdropClick = false,
  size,
}: UseApiWorkflowDialogProps) => {
  const [currentStep, setCurrentStep] = useState(ApiWorkflowDialogSteps.UserEntry);

  const onClickConfirm = useCallback(() => {
    if (onSubmit) {
      onSubmit();
    }
    setCurrentStep(ApiWorkflowDialogSteps.UserSubmitted);
  }, [onSubmit]);

  const onClickSubmit = useCallback(() => {
    if (shouldShowConfirmationStep) {
      // When the user clicks the submit button, the dialog should move to the confirmation step
      setCurrentStep(ApiWorkflowDialogSteps.UserConfirmation);
    } else {
      onClickConfirm();
    }
  }, [onClickConfirm, shouldShowConfirmationStep]);

  const onClickClose: ModalProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick' && !closeOnBackdropClick) {
      return;
    }
    onClose?.();
  };

  const onClickNotConfirm = () => {
    setCurrentStep(ApiWorkflowDialogSteps.UserEntry);
  };

  const hideActionButtons =
    hideDisplayButtonsOnSuccess &&
    currentStep === ApiWorkflowDialogSteps.UserSubmitted &&
    !submittingError &&
    !isSubmitting;

  const dialogSizeOverride =
    currentStep === ApiWorkflowDialogSteps.UserSubmitted && !submittingError && !isSubmitting
      ? 'medium'
      : size;

  const dialogSizeSx = computeDialogSize(dialogSizeOverride || 'medium');

  const { saveButton, cancelButton } = useApiWorkflowDialogButtons({
    dialogMessages,
    currentStep,
    isValidForSubmit: Boolean(isValidForSubmit),
    isInitialLoading: Boolean(isInitialLoading),
    initialLoadingError,
    isSubmitting: Boolean(isSubmitting),
    submittingError,
    saveButtonColor,
    onClose,
    onConfirm: onClickConfirm,
    onNotConfirm: onClickNotConfirm,
    onSubmit: onClickSubmit,
    onRedirectAfterSubmit,
    onRetryInitialLoadingFetch,
    onRetrySubmittingFetch: onRetrySubmit,
    successCountdownTime,
  });

  const isCloseDisabled = currentStep === ApiWorkflowDialogSteps.UserSubmitted && isSubmitting;

  return {
    dialogSizeSx,
    currentStep,
    isCloseDisabled,
    saveButton,
    cancelButton,
    onClickSubmit,
    hideActionButtons,
    onClickClose,
  };
};
